import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../../config/api';
import { formatDistanceToNow } from 'date-fns';

import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  CardMedia,
  Skeleton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import Navbar from '../components/navbar/Navbar';
import Footer from '../components/footer/Footer';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await ApiService.get(`/blogs/read/${slug}`);
        if (response.data.error) {
          throw new Error(response.data.message);
        }
        setBlog(response.data.meta);
      } catch (err) {
        console.error('Failed to fetch blog details.', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  return (
    <>
      <Navbar />
      <Box
        sx={{
          background: 'var(--bs-primary)',
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, mt: 10, mb: 3, px: { xs: 1, sm: 4, md: 8 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "var(--bs-accent)",
              mb: 3,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Blogs
          </Button>

          <Paper
            elevation={6}
            sx={{
              p: { xs: 2, sm: 4, md: 6 },
              mx: "auto",
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(12px)",
              color: "var(--bs-accent)",
            }}
          >
            {/* Title + Meta */}
            <Box sx={{ mb: 3 }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="30%" />
                </>
              ) : (
                <>
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      color: "var(--bs-accent)",
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "1.8rem", md: "2.5rem" },
                    }}
                  >
                    {blog?.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--bs-accent)" }}>
                    {blog?.createdAt
                      ? formatDistanceToNow(new Date(blog.createdAt), { addSuffix: true })
                      : "Date not available"}
                  </Typography>
                </>
              )}
            </Box>

            {/* Image */}
            <Box
              sx={{
                my: 1,
                mx: { xs: -2, sm: -4, md: -6 },
                borderRadius: "16px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
              }}
            >
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: "12px" }} />
              ) : (
                <CardMedia
                  component="img"
                  src={blog?.image}
                  alt={blog?.title}
                  sx={{
                    width: "100%",
                    maxHeight: "500px",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>

            {/* Content */}
            {loading ? (
              <Box>
                <Skeleton variant="text" width="90%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="95%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" />
              </Box>
            ) : (
              <Box
                sx={{
                  lineHeight: 1.8,
                  fontSize: "clamp(1rem, 1.1vw, 1.2rem)",
                  color: "var(--bs-secondary)",
                  "& p": { mb: 2 },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                    mt: 2,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: blog?.description }}
              />
            )}

            {/* Footer Meta */}
            <Box
              sx={{
                mt: 2,
                p: 3,
                background: "rgba(255,255,255,0.05)",
                borderRadius: "12px",
                fontSize: "0.9rem",
                color: "var(--bs-secondary)",
              }}
            >
              {loading ? (
                <>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="50%" />
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ my: 0.5 }}>
                    <strong>Created :</strong>{" "}
                    {blog?.createdAt
                      ? `${new Date(blog.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })} (${formatDistanceToNow(new Date(blog.createdAt), {
                        addSuffix: true,
                      })})`
                      : "Not available"}
                  </Typography>
                  <Typography variant="body2" sx={{ my: 0.5 }}>
                    <strong>Last Updated :</strong>{" "}
                    {blog?.updatedAt
                      ? `${new Date(blog.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })} (${formatDistanceToNow(new Date(blog.updatedAt), {
                        addSuffix: true,
                      })})`
                      : "Not available"}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default BlogDetailPage;
